import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { NewDocument } from '@/features/documents/NewDocument'
import { formatDateShort } from '@/lib/dates'
import { formatMoney, pluralise } from '@/lib/format'
import { jobName } from '@/lib/jobs'
import { describeError } from '@/lib/supabase'
import { QuoteStatusBadge } from './QuoteEditorPage'
import { useAllQuotes, type QuoteWithLines } from './queries'

export const clientOf = (row: { contact: { first_name: string; last_name: string | null } | null }) =>
  row.contact ? [row.contact.first_name, row.contact.last_name].filter(Boolean).join(' ') : 'No client'

/** Documents › Quotes (29 Oct 2026): every quote, and New quote. */
export function QuotesPage() {
  const query = useAllQuotes()
  const [params, setParams] = useSearchParams()
  const forJob = params.get('new')
  const [adding, setAdding] = useState(forJob !== null)

  return (
    <Page>
      <PageHeader
        eyebrow="Documents"
        title="Quotes"
        meta={query.data ? <span>{pluralise(query.data.length, 'quote')}</span> : null}
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            New quote
          </Button>
        }
      />
      {adding ? (
        <NewDocument
          kind="quote"
          jobId={forJob}
          onClose={() => {
            setAdding(false)
            if (forJob) setParams({}, { replace: true })
          }}
        />
      ) : null}
      <Section>
        {query.isPending ? (
          <Loading label="Loading quotes" />
        ) : query.isError ? (
          <ErrorState title="Quotes could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState title="No quotes yet" body="Click New quote to make one for a client." />
        ) : (
          <div className="overflow-x-auto">
            <table className="ledger min-w-[44rem]">
              <thead>
                <tr>
                  <th className="label">Quote</th>
                  <th className="label">Client</th>
                  <th className="label">Project</th>
                  <th className="label">Status</th>
                  <th className="label num">Total</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((quote) => (
                  <QuoteRow key={quote.id} quote={quote} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </Page>
  )
}

function QuoteRow({ quote }: { quote: QuoteWithLines }) {
  return (
    <tr>
      <td>
        <Link to={`/quotes/${quote.id}`} className="font-semibold underline-offset-4 hover:underline">
          Quote {quote.quote_number}
        </Link>
        <span className="block text-[length:var(--text-2xs)] text-taupe">{formatDateShort(quote.created_at.slice(0, 10))}</span>
      </td>
      <td>{clientOf(quote)}</td>
      <td className="text-taupe">{quote.job ? `${jobName(quote.job)} · ${formatDateShort(quote.job.event_date)}` : 'No project'}</td>
      <td>
        <QuoteStatusBadge status={quote.status} />
      </td>
      <td className="num" data-numeric>
        {formatMoney(Number(quote.total))}
      </td>
    </tr>
  )
}
